import React from "react";
import { motion } from "framer-motion";
import { MapPin, Navigation, MessageCircle, Mail } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { CONTACT } from "../data/site";

export const Location = () => {
  const { t } = useLang();
  const waLink = `https://wa.me/${CONTACT.whatsapp}`;
  const mapEmbed =
    "https://www.google.com/maps?q=Casa%20Romano%20Hostel%20San%20Juan%20del%20Sur&output=embed";

  return (
    <section id="location" className="bg-sand py-24 sm:py-32" data-testid="location-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-stretch lg:gap-14">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="flex flex-col justify-center"
          >
            <span className="text-xs font-medium uppercase tracking-[0.3em] text-teal">
              {t.location.kicker}
            </span>
            <h2 className="mt-4 font-display text-4xl font-bold leading-tight tracking-tight text-purpleink-deep sm:text-5xl">
              {t.location.title}
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-stone-600">
              {t.location.body}
            </p>

            <div className="mt-8 flex items-start gap-3 rounded-2xl border border-purpleink/12 bg-white p-5">
              <MapPin className="mt-0.5 h-5 w-5 flex-shrink-0 text-purpleink" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">
                  {t.location.addressLabel}
                </p>
                <p className="mt-1 text-sm font-medium text-stone-700">
                  {t.location.address}
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={CONTACT.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-directions-btn"
                className="flex items-center gap-2 rounded-full bg-purpleink px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-200 hover:bg-purpleink-deep active:scale-95"
              >
                <Navigation className="h-4 w-4" />
                {t.location.directions}
              </a>
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-whatsapp-btn"
                className="flex items-center gap-2 rounded-full bg-teal px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-200 hover:bg-teal-deep active:scale-95"
              >
                <MessageCircle className="h-4 w-4" />
                {t.location.whatsapp}
              </a>
            </div>

            <a
              href={`mailto:${CONTACT.email}`}
              className="mt-5 flex items-center gap-2 text-sm font-medium text-stone-500 transition-colors hover:text-purpleink"
              data-testid="location-email"
            >
              <Mail className="h-4 w-4" />
              {CONTACT.email}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative min-h-[380px] overflow-hidden rounded-[2rem] rounded-tr-none border-4 border-white shadow-2xl"
          >
            <iframe
              title="Casa Romano location map"
              src={mapEmbed}
              className="absolute inset-0 h-full w-full"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
